// IndexedDB removed for simplicity. We keep in-memory + sessionStorage + localStorage fallback.

export interface AuthTokens {
  accessToken: string;
  // refreshToken may be absent from JS (server stores it in HttpOnly cookie)
  refreshToken?: string | null;
  userId?: string;
}

export type StorageType = "persistent" | "session";

export const LOCAL_STORAGE_KEY = "auth_tokens_fallback";

class TokenService {
  private readonly TOKEN_KEY = "auth_tokens";
  private readonly REFRESH_COOKIE_NAME = "refresh_token";

  // in-memory cache for same-tab synchronous reads
  private inMemoryTokens: AuthTokens | null = null;
  // single-flight refresh promise
  private refreshPromise: Promise<boolean> | null = null;
  // monitoring counters
  private refreshAttempts = 0;
  private refreshSuccesses = 0;
  private refreshFailures = 0;
  // broadcast channel for cross-tab sync
  private bc: BroadcastChannel | null = null;

  constructor() {
    // no DB initialization required

    // Setup BroadcastChannel for cross-tab token sync if supported
    try {
      // @ts-ignore
      if (typeof BroadcastChannel !== "undefined") {
        // @ts-ignore
        this.bc = new BroadcastChannel("auth_tokens_channel");
        this.bc.addEventListener("message", (ev: any) => {
          try {
            const { type, tokens } = ev.data || {};
            if (type === "tokens" && tokens) {
              this.inMemoryTokens = tokens;
              try {
                sessionStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokens));
              } catch (e) {
                // ignore
              }
              // Inform app of auth change
              window.dispatchEvent(new CustomEvent("auth-change"));
            }
          } catch (e) {
            // ignore
          }
        });
      }
    } catch (e) {
      // ignore
    }
  }

  // No IndexedDB helper methods - simplified storage strategy

  /**
   * Improved token saving with better error handling and fallbacks
   */
  async saveTokens(
    tokens: AuthTokens,
    storageType: StorageType = "persistent"
  ): Promise<void> {

    // update in-memory copy synchronously so other callers can read immediately
    this.inMemoryTokens = tokens;

    // Always save to localStorage as a fallback
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tokens));
    } catch (e) {
      console.warn("⚠️ Failed to save tokens to localStorage fallback:", e);
    }

    // Persist to sessionStorage for current-session fast reads
    try {
      sessionStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokens));
    } catch (e) {
      console.warn("⚠️ Could not cache tokens in sessionStorage:", e);
    }

    // If persistent requested, keep localStorage fallback so tokens survive across
    // browsers that may not run the refresh flow immediately. With refresh token
    // being an HttpOnly cookie, the client can always request a fresh access token.
    if (storageType === "persistent") {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tokens));
      } catch (e) {
        console.warn("⚠️ Failed to save tokens to localStorage fallback:", e);
      }
    }

    // Broadcast new tokens to other tabs
    try {
      if (this.bc) this.bc.postMessage({ type: "tokens", tokens });
    } catch (e) {
      // ignore
    }
  }

  /**
   * Simplified and more reliable token retrieval
   * Priority: in-memory -> sessionStorage -> IndexedDB -> localStorage fallback
   */
  async getTokens(): Promise<AuthTokens | null> {
    // 0. In-memory
    if (this.inMemoryTokens) return this.inMemoryTokens;

    // 1. First try session storage (fastest, most reliable)
    try {
      const sessionTokens = sessionStorage.getItem(this.TOKEN_KEY);
      if (sessionTokens) {
        const tokens = JSON.parse(sessionTokens);
        this.inMemoryTokens = tokens;
        return tokens;
      }
    } catch (error) {
      console.warn("⚠️ Error reading from sessionStorage:", error);
    }
    // 2. Finally try localStorage fallback
    try {
      const fallbackTokens = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (fallbackTokens) {
        const tokens = JSON.parse(fallbackTokens);

        // set in-memory copy and persist correctly
        this.inMemoryTokens = tokens;
        try {
          await this.saveTokens(tokens, "persistent");
        } catch (e) {
          console.warn("⚠️ Could not restore tokens to proper storage");
        }

        return tokens;
      }
    } catch (error) {
      console.warn("⚠️ Error reading from localStorage fallback:", error);
    }

    return null;
  }

  /**
   * Get only the access token (convenience method)
   */
  async getAccessToken(): Promise<string | null> {
    // Prefer in-memory synchronous value when available
    if (this.inMemoryTokens?.accessToken) return this.inMemoryTokens.accessToken;
    const tokens = await this.getTokens();
    return tokens?.accessToken || null;
  }

  /**
   * Get only the refresh token (convenience method)
   */
  async getRefreshToken(): Promise<string | null> {
    if (this.inMemoryTokens?.refreshToken) return this.inMemoryTokens.refreshToken;
    const tokens = await this.getTokens();
    if (tokens?.refreshToken) {
      return tokens.refreshToken;
    }

    // If not found, try to get from cookie
    return this.getRefreshTokenFromCookie();
  }

  /**
   * Helper method to get refresh token from HTTP cookie
   * Note: This will only work if the cookie was set with HttpOnly: false
   * If HttpOnly: true, this will return null as JavaScript cannot access it
   */
  private getRefreshTokenFromCookie(): string | null {
    // Parse cookies from document.cookie
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === this.REFRESH_COOKIE_NAME) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  /**
   * Updates just the access token, preserving the refresh token
   */
  async updateAccessToken(accessToken: string): Promise<void> {
    const tokens = this.inMemoryTokens || (await this.getTokens());
    if (!tokens) return;

    const storageType = sessionStorage.getItem(this.TOKEN_KEY)
      ? "session"
      : "persistent";
    await this.saveTokens(
      {
        ...tokens,
        accessToken,
      },
      storageType
    );
  }

  /**
   * Clear all tokens from both storage types
   */
  async clearTokens(): Promise<void> {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (e) {
      console.warn("Failed to clear localStorage fallback:", e);
    }

    // Clear from session storage
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
    } catch (e) {
      // ignore
    }

    this.inMemoryTokens = null;

    try {
      if (this.bc) this.bc.postMessage({ type: "tokens", tokens: null });
    } catch (e) {
      // ignore
    }
  }

  /**
   * Checks if the provided token is valid (not expired)
   */
  isTokenValid(token: string | null): boolean {
    if (!token) return false;

    try {
      const decodedToken: any = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;

      return decodedToken.exp > currentTime;
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  }

  /**
   * Single-flight refresh orchestration
   * Accepts a factory that performs the refresh and returns a Promise<boolean>
   * If a refresh is already in progress, the existing promise is returned
   */
  startRefresh(refreshFactory: () => Promise<boolean>): Promise<boolean> {
    this.refreshAttempts++;
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      let result = false;
      try {
        result = await refreshFactory();
        if (result) this.refreshSuccesses++;
        else this.refreshFailures++;
        return result;
      } finally {
        // clear promise when done
        this.refreshPromise = null;
        try {
          if (this.bc) this.bc.postMessage({ type: "refresh-done", success: result });
        } catch (e) {
          // ignore
        }
      }
    })();

    return this.refreshPromise;
  }

  // Expose metrics for lightweight monitoring/testing
  getMetrics() {
    return {
      refreshAttempts: this.refreshAttempts,
      refreshSuccesses: this.refreshSuccesses,
      refreshFailures: this.refreshFailures,
    };
  }

  /** Wait for any in-flight refresh to complete. Returns boolean|null
   *  true = refresh succeeded, false = failed, null = no refresh in progress
   */
  async waitForRefreshIfAny(): Promise<boolean | null> {
    if (!this.refreshPromise) return null;
    try {
      const res = await this.refreshPromise;
      return res;
    } catch (e) {
      return false;
    }
  }

  getAccessTokenSync(): string | null {
    return this.inMemoryTokens?.accessToken || null;
  }

  getRefreshPromise(): Promise<boolean> | null {
    return this.refreshPromise;
  }

  /**
   * Extracts user ID and roles from the token
   */
  decodeToken(token: string | null): {
    userId: string | null;
    roles: string[];
  } {
    if (!token) return { userId: null, roles: [] };

    try {
      const decodedToken: any = JSON.parse(atob(token.split(".")[1]));
      return {
        userId: decodedToken.sub || null,
        roles: decodedToken["cognito:groups"] || [],
      };
    } catch (error) {
      console.error("Token decode error:", error);
      return { userId: null, roles: [] };
    }
  }
}

export const tokenService = new TokenService();
export default tokenService;
